import { ChangeDetectorRef, Component, inject, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { ChatPersistenceService, DEFAULT_SETTINGS } from '../../core/services/chat-persistence.service';
import { LmStudioService } from '../../core/services/lm-studio.service';
import { environment } from '../../../environments/environment';
import { SettingsSyncService } from '../../core/services/settings-sync.service';

@Component({
  selector: 'app-settings-page',
  imports: [FormsModule],
  templateUrl: './settings-page.component.html',
  styleUrls: ['./settings-page.component.scss']
})
export class SettingsPageComponent implements OnInit {
  private readonly router = inject(Router);
  private readonly chatPersistence = inject(ChatPersistenceService);
  private readonly lmStudio = inject(LmStudioService);
  protected readonly settingsSync = inject(SettingsSyncService);
  private readonly changeDetector = inject(ChangeDetectorRef);

  /** Canonical defaults, exposed to the template for hint text. */
  readonly defaultSettings = DEFAULT_SETTINGS;

  // Form data (empty string / undefined means "not customized" → default is used)
  mainApiUrl = DEFAULT_SETTINGS.mainApiUrl;
  openAiApiKey = '';
  savingApiKey = false;
  apiKeyStatus = '';
  model = '';
  temperature: number | null = null;
  reasoningEffort: 'low' | 'medium' | 'high' | '' = '';
  logLlmRequests = DEFAULT_SETTINGS.logLlmRequests;
  relatedTopicGenerationEnabled = DEFAULT_SETTINGS.relatedTopicGenerationEnabled;
  helperModelEnabled = DEFAULT_SETTINGS.helperModel.enabled;
  helperModelId = DEFAULT_SETTINGS.helperModel.modelId ?? '';
  helperApiUrl = '';

  // Dynamic model list from server
  modelList: string[] = [];
  helperModelList: string[] = [];
  loadingModels = false;
  loadingHelperModels = false;
  modelListError = '';

  // Validation messages
  validationMessages: { mainApiUrl?: string; helperApiUrl?: string } = {};

  private modelReloadTimer: ReturnType<typeof setTimeout> | null = null;
  
  constructor() {
    this.loadSettings();
  }

  ngOnInit(): void {
    void this.loadSyncedSettings();
    // The settings route can render before the dev proxy/LM Studio is ready.
    // Retry automatically so opening the page never requires touching the API field.
    void this.loadInitialModelLists();
  }

  get apiKeyConfigured(): boolean {
    return this.settingsSync.openAiKeyConfigured() || !!environment.apiKey.trim();
  }

  private async loadSyncedSettings(): Promise<void> {
    await this.settingsSync.load();
    this.loadSettings();
  }
  
  loadSettings(): void {
    // Load existing settings from persistence service or use defaults
    const savedSettings = this.chatPersistence.getSettings();
    this.mainApiUrl = savedSettings?.mainApiUrl || DEFAULT_SETTINGS.mainApiUrl;
    this.model = savedSettings?.model || '';
    this.temperature = savedSettings?.temperature ?? null;
    this.reasoningEffort = (savedSettings?.reasoningEffort as 'low' | 'medium' | 'high' | '') || '';
    this.logLlmRequests = savedSettings?.logLlmRequests ?? DEFAULT_SETTINGS.logLlmRequests;
    this.relatedTopicGenerationEnabled = savedSettings?.relatedTopicGenerationEnabled ?? DEFAULT_SETTINGS.relatedTopicGenerationEnabled;
    this.helperModelEnabled = savedSettings?.helperModel?.enabled ?? DEFAULT_SETTINGS.helperModel.enabled;
    this.helperModelId = savedSettings?.helperModel?.modelId ?? '';
    this.helperApiUrl = savedSettings?.helperModel?.apiUrl ?? '';
  }

  private async loadInitialModelLists(): Promise<void> {
    await this.loadWithRetry(() => this.loadModels(), () => this.modelList.length > 0);
    if (this.helperModelEnabled) {
      await this.loadWithRetry(() => this.loadHelperModels(), () => this.helperModelList.length > 0);
    }
  }

  private async loadWithRetry(load: () => Promise<void>, loaded: () => boolean): Promise<void> {
    for (const delay of [0, 500, 1_500, 3_000]) {
      if (delay) await new Promise<void>((resolve) => setTimeout(resolve, delay));
      await load();
      if (loaded()) return;
    }
  }

  /** Effective value = saved (customized) value, falling back to DEFAULT_SETTINGS. */
  get effectiveMainApiUrl(): string {
    return this.mainApiUrl || DEFAULT_SETTINGS.mainApiUrl;
  }

  get effectiveModel(): string {
    return this.model || DEFAULT_SETTINGS.model;
  }

  get effectiveTemperature(): number {
    return this.temperature ?? DEFAULT_SETTINGS.temperature;
  }

  get effectiveReasoningEffort(): 'low' | 'medium' | 'high' {
    return this.reasoningEffort || DEFAULT_SETTINGS.reasoningEffort;
  }

  /** Called when the root URL input changes — reload the model list (debounced). */
  onApiChange(): void {
    if (this.modelReloadTimer !== null) {
      clearTimeout(this.modelReloadTimer);
    }
    this.modelReloadTimer = setTimeout(() => {
      this.modelList = [];
      this.loadModels();
    }, 500);
  }

  onHelperApiChange(): void {
    if (this.modelReloadTimer !== null) clearTimeout(this.modelReloadTimer);
    this.modelReloadTimer = setTimeout(() => this.loadHelperModels(), 500);
  }

  onHelperEnabledChange(): void {
    this.persistSettings();
    if (this.helperModelEnabled) this.loadHelperModels();
  }

  /** Fetch the model list from the LM Studio server using the current root URL. */
  async loadModels(): Promise<void> {
    const apiUrl = this.effectiveMainApiUrl;
    if (!apiUrl) {
      this.modelList = [];
      this.modelListError = 'Enter a main model API to load models.';
      return;
    }
    this.loadingModels = true;
    this.modelListError = '';
    this.changeDetector.markForCheck();
    try {
      this.modelList = await this.lmStudio.listModels(apiUrl);
    } catch (err) {
      this.modelList = [];
      this.modelListError = this.extractError(err);
    } finally {
      this.loadingModels = false;
      this.changeDetector.markForCheck();
    }
  }

  async loadHelperModels(): Promise<void> {
    this.loadingHelperModels = true;
    this.changeDetector.markForCheck();
    try {
      // An empty helper endpoint deliberately inherits the main endpoint and key.
      const apiUrl = this.helperApiUrl || this.effectiveMainApiUrl;
      this.helperModelList = await this.lmStudio.listModels(apiUrl);
    } catch (err) {
      this.helperModelList = [];
      this.modelListError = this.extractError(err);
    } finally {
      this.loadingHelperModels = false;
      this.changeDetector.markForCheck();
    }
  }

  private extractError(err: unknown): string {
    if (typeof err === 'string') return err;
    if (err && typeof err === 'object') {
      const e = err as { error?: { message?: string }; message?: string };
      return e.error?.message || e.message || 'Failed to fetch models.';
    }
    return 'Failed to fetch models.';
  }

  restoreDefaults(): void {
    if (confirm('Restore all LLM settings to their default values?')) {
      this.chatPersistence.resetSettings();
      this.loadSettings();
      void this.loadInitialModelLists();
      // Re-persist so the reset state is written back (loadSettings alone only clears storage).
      this.persistSettings();
      alert('Settings restored to defaults.');
    }
  }
  
  /** Persist the current form state — called automatically on every change. */
  persistSettings(): void {
    this.validateForm();

    const settings = {
      mainApiUrl: this.effectiveMainApiUrl,
      model: this.effectiveModel,
      temperature: this.effectiveTemperature,
      reasoningEffort: this.effectiveReasoningEffort,
      logLlmRequests: this.logLlmRequests,
      relatedTopicGenerationEnabled: this.relatedTopicGenerationEnabled,
      helperModel: {
        enabled: this.helperModelEnabled,
        apiUrl: this.helperApiUrl.trim() || undefined,
        modelId: this.helperModelId || undefined,
      },
    };

    void this.settingsSync.save(settings);
  }

  async saveOpenAiKey(): Promise<void> {
    const key = this.openAiApiKey.trim();
    if (!key) return;
    this.savingApiKey = true;
    this.apiKeyStatus = '';
    try {
      await this.settingsSync.saveOpenAiKey(key);
      this.openAiApiKey = '';
      this.apiKeyStatus = 'Saved securely. The key cannot be viewed again.';
    } catch (error) {
      this.apiKeyStatus = error instanceof Error ? error.message : 'Could not save the API key.';
    } finally {
      this.savingApiKey = false;
    }
  }

  async removeOpenAiKey(): Promise<void> {
    if (!confirm('Remove the saved OpenAI API key? This cannot be undone.')) return;
    this.savingApiKey = true;
    this.apiKeyStatus = '';
    try {
      await this.settingsSync.removeOpenAiKey();
      this.apiKeyStatus = 'Saved API key removed.';
    } catch (error) {
      this.apiKeyStatus = error instanceof Error ? error.message : 'Could not remove the API key.';
    } finally {
      this.savingApiKey = false;
    }
  }
  
  validateForm(): boolean {
    this.validationMessages = {};
    
    if (this.mainApiUrl && !this.isValidUrl(this.mainApiUrl)) {
      this.validationMessages.mainApiUrl = 'Please enter a valid URL';
      return false;
    }
    if (this.helperApiUrl && !this.isValidUrl(this.helperApiUrl)) {
      this.validationMessages.helperApiUrl = 'Please enter a valid URL';
      return false;
    }
    
    return true;
  }
  
  isValidUrl(string: string): boolean {
    try {
      new URL(string);
      return true;
    } catch (_) {
      return false;
    }
  }
  
  clearChatHistory(): void {
    if (confirm('Are you sure you want to clear all chat history? This action cannot be undone.')) {
      this.chatPersistence.clearChatHistory();
      alert('Chat history cleared successfully!');
    }
  }
  
  goBack(): void {
    this.router.navigate(['/chat']);
  }
}
